// POS real-time körpüsü — Laravel Echo singleton-unu POS rejimində "wrap" edir.
//
// Front masa grid-i (`pages/Masalar.tsx`) `useTableRealtimeWithFallback` vasitəsilə
// `echo.channel('restaurant.<id>.tables').listen('.table.status-changed', ...)` dinləyir.
// Cloud rejimində bu kanal Reverb-dən gəlir; POS rejimində isə həmin kanalı
// pos_local WS event-ləri ilə qidalandırırıq — beləliklə komponent DƏYİŞMƏDƏN
// real-time alır.
//
// Yalnız `*.tables` kanalları POS WS-ə yönlənir. Qalan bütün kanallar (QR-menu,
// waiter-call və s.) və `connector` toxunulmadan cloud echo-ya delegate olunur
// (hibrid prinsip: POS-əməliyyat → lokal, admin/QR → cloud).
//
// pos_local domain event-i (orders.*/tables.*) gələndə cari masa vəziyyətini
// gateway-in öz `GET /tables` route-u ilə yenidən çəkirik (totals daxil) və hər
// masa üçün `.table.status-changed` yayırıq — beləliklə HTTP yolu ilə tam eyni
// shape qaytarılır (təkrar məntiq yoxdur).

import { matchPosRoute } from './routes';
import { getPosWs, PosEnvelope } from './posWs';

/** `restaurant.<id>.tables` kimi POS-əməliyyat kanalı? */
function isTablesChannel(name: string): boolean {
  return /\.tables$/.test(name);
}

/** pos_local event-i masa grid-inə təsir edirmi? */
function affectsTables(type: string): boolean {
  return type.startsWith('orders.') || type.startsWith('tables.');
}

const STATUS_EVENT = '.table.status-changed';
const REFETCH_DEBOUNCE_MS = 200;

type Listener = (data: unknown) => void;

interface PosChannel {
  listen(event: string, cb: Listener): PosChannel;
  stopListening(event: string, cb?: Listener): PosChannel;
}

export interface PosRealtimeDeps {
  /** pos domain event-lərinə abunə ol; unsubscribe qaytarır. */
  subscribe(onEvent: (e: PosEnvelope) => void): () => void;
  /** Cari masaları cloud shape-ində çək (gateway `GET /tables` ilə eyni). */
  fetchTables(): Promise<Array<Record<string, unknown>>>;
}

/** Defolt: real pos_local WS + gateway-in GET /tables route-u. */
function defaultDeps(): PosRealtimeDeps {
  return {
    subscribe: (onEvent) => getPosWs().onEvent(onEvent),
    fetchTables: async () => {
      const route = matchPosRoute({
        method: 'GET',
        pathname: '/tables',
        query: {},
        body: null,
      });
      if (!route) return [];
      const res = await route.run({
        method: 'GET',
        pathname: '/tables',
        query: {},
        body: null,
      });
      const data = res.data as { tables?: Array<Record<string, unknown>> };
      return data?.tables ?? [];
    },
  };
}

/** Cloud masa obyektini `.table.status-changed` payload-ına çevir. */
function toStatusPayload(ct: Record<string, unknown>): Record<string, unknown> {
  return {
    table_id: ct.id,
    table_name: ct.name,
    is_available: ct.is_available, // 0/1
    book_time: ct.book_time ?? null,
    user_name: ct.user_name ?? null,
    total_price: ct.total_price ?? 0,
    occurred_at: new Date().toISOString(),
  };
}

class ChannelImpl implements PosChannel {
  private listeners = new Map<string, Set<Listener>>();

  listen(event: string, cb: Listener): PosChannel {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb);
    return this;
  }

  stopListening(event: string, cb?: Listener): PosChannel {
    if (!cb) {
      this.listeners.delete(event);
    } else {
      this.listeners.get(event)?.delete(cb);
    }
    return this;
  }

  emit(event: string, data: unknown): void {
    const set = this.listeners.get(event);
    if (set) for (const cb of set) cb(data);
  }
}

/**
 * Cloud Echo singleton-unu POS WS ilə "wrap" edir.
 * `*.tables` kanalları lokal qidalanır; qalan hər şey cloud-a delegate olunur.
 */
export function createPosEcho<T extends object>(
  cloudEcho: T,
  deps: PosRealtimeDeps = defaultDeps(),
): T {
  const channels = new Map<string, ChannelImpl>();
  let unsubscribe: (() => void) | null = null;
  let refetchTimer: ReturnType<typeof setTimeout> | null = null;

  const broadcast = (payloads: Array<Record<string, unknown>>) => {
    for (const ct of payloads) {
      const data = toStatusPayload(ct);
      for (const ch of channels.values()) ch.emit(STATUS_EVENT, data);
    }
  };

  const refetchAndEmit = async () => {
    try {
      const tables = await deps.fetchTables();
      broadcast(tables);
    } catch {
      /* offline/keçici xəta — növbəti event və ya polling fallback tutar */
    }
  };

  const scheduleRefetch = () => {
    if (refetchTimer) return; // burst-ları birləşdir
    refetchTimer = setTimeout(() => {
      refetchTimer = null;
      void refetchAndEmit();
    }, REFETCH_DEBOUNCE_MS);
  };

  const ensureSubscribed = () => {
    if (unsubscribe) return;
    unsubscribe = deps.subscribe((e) => {
      if (affectsTables(e.type)) scheduleRefetch();
    });
    // İlk qoşulmada cari vəziyyəti dərhal sinxronla.
    void refetchAndEmit();
  };

  const maybeUnsubscribe = () => {
    if (channels.size > 0 || !unsubscribe) return;
    unsubscribe();
    unsubscribe = null;
    if (refetchTimer) {
      clearTimeout(refetchTimer);
      refetchTimer = null;
    }
  };

  return new Proxy(cloudEcho, {
    get(target, prop, receiver) {
      if (prop === 'channel') {
        return (name: string) => {
          if (!isTablesChannel(name)) {
            return (target as { channel(n: string): unknown }).channel(name);
          }
          let ch = channels.get(name);
          if (!ch) {
            ch = new ChannelImpl();
            channels.set(name, ch);
            ensureSubscribed();
          }
          return ch;
        };
      }
      if (prop === 'leaveChannel') {
        return (name: string) => {
          if (isTablesChannel(name)) {
            channels.delete(name);
            maybeUnsubscribe();
            return undefined;
          }
          return (target as { leaveChannel(n: string): unknown }).leaveChannel(name);
        };
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}
